///
/// EarthBrowser
///
/// Created by Matt Giger on 10/30/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Render-to-texture pass that bakes the cloud overlay's current frame
/// pair, flow field and blend fraction into one R8 "cold index" field
/// (cloud_field.glsl). overlay_clouds.glsl takes ~20 taps per fragment
/// into the cloud field (parallax march, relief gradients, shadows); each
/// used to be two fetches plus the advection math, and the tile pass was
/// measured to be bound by that fetch count. Sampling a baked field makes
/// each tap one fetch at the cost of one pass at frame resolution whenever
/// the inputs change.
///
/// Runs from CloudsOverlay.frameUpdate, i.e. during the render traversal
/// before the frame's draw calls, so it restores the GL state it touches
/// (the same contract as FramePairPacker). The blend is quantized so a
/// clock drifting in live mode rebakes about once a minute rather than
/// every frame; animated playback rebakes as often as the blend moves.
///
/// An optional second pair (the global forecast clouds) is mixed over the
/// first by a weight inside the same pass (cloud_field.glsl), so the
/// observed-to-forecast hand-off costs the tile shader nothing.
///

import { FrameBuffer } from "@stovokor/shader/FrameBuffer"
import { ComponentType, DrawMode, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { type ShaderProgram } from "@stovokor/shader/ShaderProgram"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Blend resolution of the bake. Hourly frames drift 1/3600 per second in
// live mode, so 1/64 steps rebake every ~56 s; the step is a change of at
// most 4 levels of an 8-bit field between two frames an hour apart
const cBlendSteps = 64
// Fastest a moving blend alone may rebake. A 4096x2048 pass plus its mip
// chain is ~3 ms of GPU, which at the 1 h/s and faster playback presets
// (the blend sweeps 0..1 within a second) would otherwise run every frame
// and give back what the single-fetch taps save. Cloud motion advancing at
// 20 Hz under a 60 Hz camera isn't visible; a frame-pair or flow change
// still bakes at once so a newly loaded frame never waits
const cMinBlendBakeIntervalMs = 50

/** The forecast series' advected pair and its share of the baked field */
export interface ForecastPair {
	frame0: Texture
	frame1: Texture
	flow: Texture
	blend: number
	/** 0..1 mix over the observed pair; 0 means not sampled */
	weight: number
}

// Snapshot of the forecast inputs a bake used, for the dirty check (texture
// identity and upload state, blend / weight at the bake's quantization)
interface ForecastBaked {
	frame0: Texture
	frame1: Texture
	flow: Texture
	available: [boolean, boolean, boolean]
	blend: number
	weight: number
}

function quantize(value: number): number {
	return Math.round(value * cBlendSteps) / cBlendSteps
}

function forecastSnapshot(pair: ForecastPair | undefined): ForecastBaked | undefined {
	if(!pair || pair.weight <= 0) return undefined
	return {
		frame0: pair.frame0,
		frame1: pair.frame1,
		flow: pair.flow,
		available: [pair.frame0.available, pair.frame1.available, pair.flow.available],
		blend: quantize(pair.blend),
		weight: quantize(pair.weight),
	}
}

function forecastChanged(a: ForecastBaked | undefined, b: ForecastBaked | undefined): boolean {
	if(a === undefined || b === undefined) return a !== b
	return a.frame0 !== b.frame0 || a.frame1 !== b.frame1 || a.flow !== b.flow
		|| a.available[0] !== b.available[0] || a.available[1] !== b.available[1] || a.available[2] !== b.available[2]
		|| a.blend !== b.blend || a.weight !== b.weight
}

export class CloudFieldBaker {
	/**
	 * The baked field: R8, mipmapped, wrapping in longitude like the
	 * global frames. A 1x1 zero texture until the first bake
	 */
	public readonly field: Texture

	private context: StovokorContext
	private shader: ShaderProgram
	private framebuffer: FrameBuffer
	private position: VertexAttribute
	private vertexBuffer: VertexBuffer

	// Inputs of the last bake, so unchanged inputs cost nothing per frame
	private baked0?: Texture
	private baked1?: Texture
	private bakedFlow?: Texture
	private bakedAvailable0 = false
	private bakedAvailable1 = false
	private bakedFlowAvailable = false
	private bakedBlend = -1
	private lastBakeMs = -Infinity
	// Forecast pair inputs of the last bake (undefined while weight was 0)
	private bakedForecast?: ForecastBaked

	constructor(context: StovokorContext) {
		this.context = context
		this.shader = context.getCachedShader(new ShaderSourceDefinition("cloud_field"))
		this.framebuffer = new FrameBuffer(context)
		this.position = new VertexAttribute(context, 2, [-1, -1, 3, -1, -1, 3])
		this.vertexBuffer = new VertexBuffer(context, [this.position])
		this.field = new Texture(context, 1, 1,
			InternalFormat.R8, PixelFormat.Red, ComponentType.UnsignedByte,
			TextureFilter.Linear, TextureFilter.Linear,
			TextureWrap.Repeat, TextureWrap.ClampToEdge)
	}

	public destroy(): void {
		this.framebuffer.destroy()
		this.vertexBuffer.destroy()
		this.field.destroy()
	}

	/**
	 * Drop the baked field back to its 1x1 zero state (the overlay was
	 * purged); the next update bakes whatever the series holds then
	 */
	public reset(): void {
		if(this.field.width !== 1 || this.field.height !== 1) {
			this.field.setFilters(TextureFilter.Linear, TextureFilter.Linear)
			this.field.initialize(1, 1)
		}
		this.field.available = false
		this.baked0 = undefined
		this.baked1 = undefined
		this.bakedFlow = undefined
		this.bakedBlend = -1
		this.lastBakeMs = -Infinity
		this.bakedForecast = undefined
	}

	/**
	 * Rebake if the frame pair, flow texture, (quantized) blend or the
	 * forecast pair / weight changed since the last bake. The field takes
	 * frame0's resolution (frame1 normally matches; a blank 1x1 frame reads
	 * as zero). Returns true when a bake ran this call.
	 */
	public update(frame0: Texture, frame1: Texture, flow: Texture, blend: number, forecast?: ForecastPair): boolean {
		const quantized = quantize(blend)
		const snapshot = forecastSnapshot(forecast)
		const inputsChanged = frame0 !== this.baked0 || frame1 !== this.baked1 || flow !== this.bakedFlow
			|| frame0.available !== this.bakedAvailable0 || frame1.available !== this.bakedAvailable1
			|| flow.available !== this.bakedFlowAvailable
			|| forecastChanged(snapshot, this.bakedForecast)
		if(!inputsChanged && quantized === this.bakedBlend) {
			return false
		}
		const now = performance.now()
		if(!inputsChanged && now - this.lastBakeMs < cMinBlendBakeIntervalMs) {
			// Blend-only change inside the rate limit: the next update past
			// it still sees the quantized value differ and bakes then
			return false
		}
		if(!this.bake(frame0, frame1, flow, quantized, snapshot ? { ...forecast!, blend: snapshot.blend, weight: snapshot.weight } : undefined)) {
			return false
		}
		this.lastBakeMs = now
		this.baked0 = frame0
		this.baked1 = frame1
		this.bakedFlow = flow
		this.bakedAvailable0 = frame0.available
		this.bakedAvailable1 = frame1.available
		this.bakedFlowAvailable = flow.available
		this.bakedBlend = quantized
		this.bakedForecast = snapshot
		return true
	}

	private bake(frame0: Texture, frame1: Texture, flow: Texture, blend: number, forecast?: ForecastPair): boolean {
		if(!this.shader.available || !frame0.textureId || !frame1.textureId || !flow.textureId || !this.field.textureId) {
			return false
		}
		// With no forecast the pair samplers still need a bound texture;
		// the shader skips them at weight 0
		const fc = forecast ?? { frame0, frame1, flow, blend: 0, weight: 0 }
		if(!fc.frame0.textureId || !fc.frame1.textureId || !fc.flow.textureId) {
			return false
		}

		const width = Math.max(frame0.width, 1)
		const height = Math.max(frame0.height, 1)
		if(this.field.width !== width || this.field.height !== height) {
			// Level 0 only here; generateMipmaps below builds the chain and
			// switches the min filter to trilinear
			this.field.setFilters(TextureFilter.Linear, TextureFilter.Linear)
			this.field.initialize(width, height)
		}

		// Save/restore through the engine's state cache: no getParameter
		// round trip, and the cache stays exact for the next frame
		const glState = this.context.glState
		const viewport = glState.getViewport()

		try {
			this.framebuffer.attach(WebGL2RenderingContext.COLOR_ATTACHMENT0, this.field)
			this.framebuffer.bind()
		} catch(error) {
			this.framebuffer.unbind()
			this.context.logger.warn("CloudFieldBaker: framebuffer incomplete", error)
			return false
		}
		glState.viewport(0, 0, width, height)
		glState.disable(WebGL2RenderingContext.DEPTH_TEST)
		glState.disable(WebGL2RenderingContext.BLEND)

		this.shader.bindAction()()
		this.shader.setupAttributeActionsFrom({ position: this.position }).forEach(action => action())
		this.shader.uniforms.frame0?.bindingFunction(frame0)()
		this.shader.uniforms.frame1?.bindingFunction(frame1)()
		this.shader.uniforms.flow?.bindingFunction(flow)()
		this.shader.uniforms.blend?.bindingFunction(blend)()
		this.shader.uniforms.forecast0?.bindingFunction(fc.frame0)()
		this.shader.uniforms.forecast1?.bindingFunction(fc.frame1)()
		this.shader.uniforms.forecastFlow?.bindingFunction(fc.flow)()
		this.shader.uniforms.forecastBlend?.bindingFunction(fc.blend)()
		this.shader.uniforms.forecastWeight?.bindingFunction(fc.weight)()
		this.vertexBuffer.draw(DrawMode.Triangles)
		this.shader.disableAttributeActions().forEach(action => action())
		this.shader.unbindAction()()

		this.framebuffer.unbind()
		glState.enable(WebGL2RenderingContext.DEPTH_TEST)
		glState.enable(WebGL2RenderingContext.BLEND)
		glState.viewport(viewport[0], viewport[1], viewport[2], viewport[3])

		// The overlay samples the field minified from orbit with explicit
		// LODs, so the chain is required, not optional
		this.field.generateMipmaps()
		this.field.available = true
		return true
	}
}
